import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const notificationKeys = {
  all: ['notifications'],
  my: (filters) => [...notificationKeys.all, 'me', filters],
  myUnreadCount: (filters) => [...notificationKeys.all, 'me', 'unread-count', filters],
  events: (filters) => [...notificationKeys.all, 'events', filters],
  event: (uuid) => [...notificationKeys.all, 'event', uuid],
  rules: (filters) => [...notificationKeys.all, 'rules', filters],
  configs: (filters) => [...notificationKeys.all, 'configs', filters],
  jobs: (filters) => [...notificationKeys.all, 'jobs', filters],
  health: (filters) => [...notificationKeys.all, 'health', filters],
};

export const sanitizeNotificationFilters = (filters = {}) => {
  const clean = { ...filters };
  Object.keys(clean).forEach((key) => {
    const value = clean[key];
    if (value === '' || value === null || value === undefined || value === 'all') {
      delete clean[key];
    }
  });
  return clean;
};

export const fetchNotificationEvents = async (filters = {}) => {
  const response = await apiClient.get('notifications/events', {
    params: sanitizeNotificationFilters(filters),
  });
  return response.data;
};

export const useMyNotifications = (filters = {}, options = {}) =>
  useQuery({
    queryKey: notificationKeys.my(filters),
    queryFn: async () => {
      const response = await apiClient.get('notifications/me', {
        params: sanitizeNotificationFilters(filters),
      });
      return response.data;
    },
    keepPreviousData: true,
    staleTime: 30 * 1000,
    ...options,
  });

export const useMyNotificationsUnreadCount = (filters = {}, options = {}) =>
  useQuery({
    queryKey: notificationKeys.myUnreadCount(filters),
    queryFn: async () => {
      const response = await apiClient.get('notifications/me/unread-count', {
        params: sanitizeNotificationFilters(filters),
      });
      return response.data;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    ...options,
  });

export const useNotificationEvents = (filters = {}) =>
  useQuery({
    queryKey: notificationKeys.events(filters),
    queryFn: () => fetchNotificationEvents(filters),
    keepPreviousData: true,
    staleTime: 30 * 1000,
  });

export const useMarkMyNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (uuid) => {
      const response = await apiClient.patch(`notifications/me/${uuid}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useMarkMyNotificationUnread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (uuid) => {
      const response = await apiClient.patch(`notifications/me/${uuid}/unread`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useMarkAllMyNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload = {}) => {
      const response = await apiClient.patch('notifications/me/read-all', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useNotificationEvent = (uuid) =>
  useQuery({
    queryKey: notificationKeys.event(uuid),
    queryFn: async () => {
      const response = await apiClient.get(`notifications/events/${uuid}`);
      return response.data;
    },
    enabled: Boolean(uuid),
  });

export const useNotificationRules = (filters = {}) =>
  useQuery({
    queryKey: notificationKeys.rules(filters),
    queryFn: async () => {
      const response = await apiClient.get('notifications/rules', {
        params: sanitizeNotificationFilters(filters),
      });
      return response.data;
    },
    staleTime: 60 * 1000,
  });

export const useNotificationConfigs = (filters = {}) =>
  useQuery({
    queryKey: notificationKeys.configs(filters),
    queryFn: async () => {
      const response = await apiClient.get('notifications/configs', {
        params: sanitizeNotificationFilters(filters),
      });
      return response.data;
    },
    staleTime: 60 * 1000,
  });

export const useNotificationJobRuns = (filters = {}) =>
  useQuery({
    queryKey: notificationKeys.jobs(filters),
    queryFn: async () => {
      const response = await apiClient.get('notifications/job-runs', {
        params: sanitizeNotificationFilters(filters),
      });
      return response.data;
    },
    keepPreviousData: true,
    staleTime: 30 * 1000,
  });

export const useNotificationHealth = (filters = {}) =>
  useQuery({
    queryKey: notificationKeys.health(filters),
    queryFn: async () => {
      const response = await apiClient.get('notifications/health', {
        params: sanitizeNotificationFilters(filters),
      });
      return response.data;
    },
    staleTime: 30 * 1000,
  });

export const useCreateNotificationEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('notifications/events', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useUpdateNotificationRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuid, payload }) => {
      const response = await apiClient.patch(`notifications/rules/${uuid}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useUpdateNotificationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuid, payload }) => {
      const response = await apiClient.patch(`notifications/configs/${uuid}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useAddNotificationConfigUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuidConfig, payload }) => {
      const response = await apiClient.post(`notifications/configs/${uuidConfig}/users`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useUpdateNotificationConfigUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuid, payload }) => {
      const response = await apiClient.patch(`notifications/configs/users/${uuid}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useRunNotificationRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuid, dryRun = true }) => {
      const response = await apiClient.post(`notifications/rules/${uuid}/run`, {
        dry_run: dryRun,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useDetectCompletedProjectMissingOperationalData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dryRun = false } = {}) => {
      const response = await apiClient.post(
        'notifications/detect/completed-project-contracts-missing-operational-data',
        { dry_run: dryRun }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useDetectCouponDueTomorrow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dryRun = false } = {}) => {
      const response = await apiClient.post('notifications/detect/coupon-due-tomorrow', {
        dry_run: dryRun,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useDetectCouponCollectionByDue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload = {}) => {
      const response = await apiClient.post('notifications/detect/coupon-collection-by-due', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useChangeNotificationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuid, action, payload = {} }) => {
      const response = await apiClient.patch(`notifications/events/${uuid}/${action}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

export const useRetryNotificationEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuid, payload = {} }) => {
      const response = await apiClient.post(`notifications/events/${uuid}/retry`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};
